(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';
  const TRANSITION_MS = 400;
  const DRAG_THRESHOLD = 24;

  const initControl = (root) => {
    if (root.dataset.nanoControlReady === 'true') return;
    root.dataset.nanoControlReady = 'true';

    const viewport = root.querySelector('.nano-control__viewport');
    const track = root.querySelector('[data-nano-control-track]');
    const cards = Array.from(track ? track.querySelectorAll('.nano-control__card') : []);
    const prevButton = root.querySelector('[data-common-carousel-arrow="prev"]');
    const nextButton = root.querySelector('[data-common-carousel-arrow="next"]');
    const dots = Array.from(root.querySelectorAll('[data-nano-control-dot]'));

    if (!viewport || !track || cards.length === 0 || !prevButton || !nextButton) return;

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    let activeIndex = 0;
    let draggableInstance = null;

    const updateDots = () => {
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const getCurrentX = () => {
      const val = window.gsap?.getProperty(track, 'x');
      return typeof val === 'number' ? val : 0;
    };

    const getPadInline = () =>
      parseFloat(window.getComputedStyle(track).paddingInlineStart) || 0;

    const computeMaxX = () => {
      const padInline = getPadInline();
      const firstLeft = cards[0].getBoundingClientRect().left;
      const viewportLeft = viewport.getBoundingClientRect().left;
      return getCurrentX() + viewportLeft + padInline - firstLeft;
    };

    const computeMinX = () => {
      const padInline = getPadInline();
      const lastRight = cards[cards.length - 1].getBoundingClientRect().right;
      const viewportRight = viewport.getBoundingClientRect().right;
      return getCurrentX() + (viewportRight - padInline) - lastRight;
    };

    const computeBounds = () => {
      const maxX = computeMaxX();
      const minX = computeMinX();
      return { minX: Math.min(minX, maxX), maxX: Math.max(minX, maxX) };
    };

    const getDragLimit = () => {
      const card = cards[activeIndex];
      return card ? card.getBoundingClientRect().width : viewport.clientWidth;
    };

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    const setArrowDisabled = (btn, disabled) => {
      if (!btn) return;
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', String(disabled));
    };

    const clampToBounds = (x, bounds = computeBounds()) =>
      Math.max(bounds.minX, Math.min(bounds.maxX, x));

    const getCardTargetX = (card, bounds = computeBounds()) => {
      const padInline = getPadInline();
      const rawTargetX = -(card.offsetLeft - padInline);
      return clampToBounds(rawTargetX, bounds);
    };

    const getClosestSnapIndex = (x = getCurrentX()) => {
      const bounds = computeBounds();
      let closestIndex = activeIndex;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const targetX = getCardTargetX(card, bounds);
        const distance = Math.abs(targetX - x);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const updatePositionState = (x = getCurrentX()) => {
      const bounds = computeBounds();
      activeIndex = getClosestSnapIndex(x);
      updateDots();
      setArrowDisabled(prevButton, x >= bounds.maxX - 1);
      setArrowDisabled(nextButton, x <= bounds.minX + 1);
    };

    const setActive = (index, animate = true) => {
      if (!mediaQuery.matches || !window.gsap || !window.Draggable) return;

      activeIndex = Math.max(0, Math.min(cards.length - 1, index));
      updateDots();

      const padInline = getPadInline();
      const card = cards[activeIndex];
      const rawTargetX =
        getCurrentX() + viewport.getBoundingClientRect().left + padInline - card.getBoundingClientRect().left;

      const bounds = computeBounds();
      const targetX = Math.max(bounds.minX, Math.min(bounds.maxX, rawTargetX));

      setArrowDisabled(prevButton, targetX >= bounds.maxX - 1);
      setArrowDisabled(nextButton, targetX <= bounds.minX + 1);

      draggableInstance?.[0]?.disable();
      window.gsap.to(track, {
        x: targetX,
        duration: animate ? TRANSITION_MS / 1000 : 0,
        ease: 'power2.out',
        overwrite: true,
        onComplete: () => {
          draggableInstance?.[0]?.enable();
        },
      });
    };

    const initMobileDrag = () => {
      if (!window.gsap || !window.Draggable) return;

      let dragBounds = null;
      let dragStartTrackX = 0;

      draggableInstance = window.Draggable.create(track, {
        type: 'x',
        minimumMovement: 5,
        cursor: 'grab',
        activeCursor: 'grabbing',
        onDragStart() {
          window.gsap.killTweensOf(track);
          dragBounds = computeBounds();
          dragStartTrackX = getCurrentX();
        },
        onDrag() {
          if (!dragBounds) return;
          const dragLimit = getDragLimit();
          const x = getCurrentX();
          const limitedX = Math.max(
            Math.max(dragBounds.minX, dragStartTrackX - dragLimit),
            Math.min(Math.min(dragBounds.maxX, dragStartTrackX + dragLimit), x)
          );
          if (limitedX !== x) window.gsap.set(track, { x: limitedX });
        },
        onDragEnd() {
          dragBounds = null;
          const pointerDelta = this.endX - this.startX;
          if (Math.abs(pointerDelta) >= DRAG_THRESHOLD) {
            setActive(activeIndex + (pointerDelta < 0 ? 1 : -1));
          } else {
            setActive(activeIndex);
          }
        },
      });

      setActive(0, false);
    };

    const teardownMobileDrag = () => {
      if (draggableInstance && draggableInstance[0]) {
        draggableInstance[0].kill();
        draggableInstance = null;
      }
      window.gsap?.set(track, { clearProps: 'x,transform' });
      activeIndex = 0;
      updateDots();
    };

    const syncMode = () => {
      teardownMobileDrag();
      if (mediaQuery.matches) initMobileDrag();
    };

    prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (prevButton.disabled) return;
      setActive(activeIndex - 1);
    });

    nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (nextButton.disabled) return;
      setActive(activeIndex + 1);
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const dotIndex = Number.parseInt(dot.dataset.nanoControlDotIndex || '0', 10);
        setActive(dotIndex);
      });
    });

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (!mediaQuery.matches || cards.length < 2) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();

        window.gsap.killTweensOf(track);
        window.gsap.set(track, { x: clampToBounds(getCurrentX() - deltaX) });
        updatePositionState();
      },
      { passive: false }
    );

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => setActive(activeIndex, false), 150);
    });

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncMode);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(syncMode);
    }

    syncMode();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-nano-control]').forEach(initControl);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
